import { useIntl } from '@doxyme/locale';
import {
  SegmentedControl,
  Select,
  type Option,
  SubscriptionBadge,
} from '@doxyme/ui/components/foundations';
import { VisibleOnly } from '@doxyme/ui/components/misc';
import { useCallback, useMemo, type JSX } from 'react';

export enum InviteViaEmailTabs {
  SendFromDoxyme = 'SendFromDoxyme',
  SendFromMyEmail = 'SendFromMyEmail',
}

export type InviteViaEmailTabPanelProps = {
  isPaidUser: boolean;
  activeTab: InviteViaEmailTabs;
  changeTab: (tab: InviteViaEmailTabs) => void;
};

const options = [
  {
    value: InviteViaEmailTabs.SendFromDoxyme,
    label: 'inviteViaEmail.doxymeTab',
    isPaidBadge: true,
  },
  {
    value: InviteViaEmailTabs.SendFromMyEmail,
    label: 'inviteViaEmail.emailTab',
  },
];

export const InviteViaEmailTabPanel = (
  props: InviteViaEmailTabPanelProps,
): JSX.Element => {
  const { isPaidUser, activeTab, changeTab } = props;

  const { formatMessage } = useIntl();

  const selectOptions = useMemo<Option[]>(
    () =>
      options.map(({ value, label }) => ({
        label: formatMessage({ id: label }),
        value,
      })),
    [formatMessage],
  );

  const selectedOption = useMemo<Option | undefined>(
    () => selectOptions.find(({ value }) => value === activeTab),
    [activeTab, selectOptions],
  );

  const onChangeSelect = useCallback(
    (option: Option) => {
      changeTab(option.value as InviteViaEmailTabs);
    },
    [changeTab],
  );

  return (
    <>
      <VisibleOnly smallViewport>
        <Select
          options={selectOptions}
          onChange={onChangeSelect}
          value={selectedOption?.label}
        />
      </VisibleOnly>
      <VisibleOnly largeViewport mediumViewport>
        <SegmentedControl
          options={options.map(({ value, label, isPaidBadge = false }) => ({
            id: value,
            label: (
              <>
                {formatMessage({ id: label })}
                {isPaidBadge && !isPaidUser && (
                  <SubscriptionBadge
                    content={formatMessage({ id: 'pro' })}
                    active={activeTab === InviteViaEmailTabs.SendFromDoxyme}
                  />
                )}
              </>
            ),
          }))}
          value={activeTab}
          onSelect={changeTab}
        />
      </VisibleOnly>
    </>
  );
};
